import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { confirmPasswordReset } from '../../services/api';

const FormContainer = styled.div`
  max-width: 460px;
  margin: 2rem auto;
  padding: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: ${props => props.theme.shadow};
  background-color: ${props => props.theme.cardBg};
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 0.75rem;
  color: ${props => props.theme.primaryText};
`;

const Hint = styled.p`
  text-align: center;
  margin: 0 0 1.1rem;
  font-size: 0.9rem;
  color: ${props => props.theme.secondaryText};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: ${props => props.theme.primaryText};
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 0.25rem;
  font-size: 1rem;
  background-color: ${props => props.theme.inputBg};
  color: ${props => props.theme.primaryText};
`;

const Button = styled.button`
  margin-top: 0.35rem;
  padding: 0.75rem;
  background-color: #4299e1;
  color: #fff;
  border: none;
  border-radius: 0.25rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  &:hover { background-color: #3182ce; }
  &:disabled { opacity: 0.7; cursor: not-allowed; }
`;

const Message = styled.div`
  margin-top: 0.9rem;
  font-size: 0.9rem;
  color: ${props => props.$error ? (props.theme.error || '#e53e3e') : props.theme.secondaryText};
  text-align: center;
`;

const BackLink = styled.div`
  text-align: center;
  margin-top: 1rem;
  font-size: 0.9rem;
  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
    font-weight: 500;
  }
  a:hover {
    text-decoration: underline;
  }
`;

function ResetPasswordForm() {
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!token) {
      setError('Reset link is invalid or missing token.');
      return;
    }
    if (!newPassword || !confirm) {
      setError('Please complete both password fields.');
      return;
    }
    if (newPassword !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await confirmPasswordReset(token, newPassword);
      setMessage(res?.message || 'Password reset successful. You can now log in.');
      setTimeout(() => navigate('/login'), 1200);
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormContainer theme={theme}>
      <Title theme={theme}>Reset Password</Title>
      <Hint theme={theme}>
        Use a strong password with at least 8 characters, uppercase, lowercase, and a number.
      </Hint>
      <Form onSubmit={onSubmit}>
        <Label theme={theme} htmlFor="new-password">New password</Label>
        <Input
          id="new-password"
          theme={theme}
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
        />
        <Label theme={theme} htmlFor="confirm-password">Confirm password</Label>
        <Input
          id="confirm-password"
          theme={theme}
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
        />
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Resetting...' : 'Reset password'}
        </Button>
      </Form>
      {message && <Message theme={theme}>{message}</Message>}
      {error && <Message theme={theme} $error>{error}</Message>}
      <BackLink theme={theme}>
        <Link to="/login">Back to login</Link>
      </BackLink>
    </FormContainer>
  );
}

export default ResetPasswordForm;
